import {
  BE_CALL_ALERTING,
  BE_CALL_ANSWERED,
  BE_HOLD_CALL,
  BE_END_CALL,
  BE_RETRIEVE_CALL,
} from '@rsos/constants/callStatuses';
import { SOURCE_SMS } from '@rsos/constants/locationSources';
import { LANDLINE, VOIP, TEXT, OTHER } from '@rsos/constants/locationTypes';
import AllCallMarker from '../assets/calls/allcall_marker.png';
import AllCallMarkerHover from '../assets/calls/allcall_marker_hover.png';
import AllCallMarkerHoverTransparent from '../assets/calls/allcall_marker_hover_transparent.png';
import AllCallMarkerTransparent from '../assets/calls/allcall_marker_transparent.png';
import AllCallSMS from '../assets/calls/allcall_textto911.png';
import AllCallSMSHover from '../assets/calls/allcall_textto911_hover.png';
import AllCallSMSTransparentHover from '../assets/calls/allcall_textto911_hover_transparent.png';
import AllCallSMSTransparent from '../assets/calls/allcall_textto911_transparent.png';
import CallSecondary from '../assets/calls/call_ali.png';
import CallLandline from '../assets/calls/call_landline.png';
import CallSelected from '../assets/calls/call_smartphone.png';
import CallStatusAnswered from '../assets/calls/call_status_answered.png';
import CallStatusAnsweredHover from '../assets/calls/call_status_answered_hover.png';
import CallStatusAnsweredTransparentHover from '../assets/calls/call_status_answered_hover_transparent.png';
import CallStatusAnsweredTransparent from '../assets/calls/call_status_answered_transparent.png';
import CallStatusOnHold from '../assets/calls/call_status_on_hold.png';
import CallStatusOnHoldHover from '../assets/calls/call_status_on_hold_hover.png';
import CallStatusOnHoldTransparentHover from '../assets/calls/call_status_on_hold_hover_transparent.png';
import CallStatusOnHoldTransparent from '../assets/calls/call_status_on_hold_transparent.png';
import CallStatusReleased from '../assets/calls/call_status_released.png';
import CallStatusReleasedHover from '../assets/calls/call_status_released_hover.png';
import CallStatusReleasedTransparentHover from '../assets/calls/call_status_released_hover_transparent.png';
import CallStatusReleasedTransparent from '../assets/calls/call_status_released_transparent.png';
import CallStatusRinging from '../assets/calls/call_status_ringing.png';
import CallStatusRingingHover from '../assets/calls/call_status_ringing_hover.png';
import CallStatusRingingTransparentHover from '../assets/calls/call_status_ringing_hover_transparent.png';
import CallStatusRingingTransparent from '../assets/calls/call_status_ringing_transparent.png';
import CallVoip from '../assets/calls/call_voip.png';
import HistoricMarker from '../assets/calls/historic_marker.png';
import HistoricMarkerHover from '../assets/calls/historic_marker_hover.png';
import SMSSelected from '../assets/calls/sms_selected.png';
import { isIgnoreType } from './ignoreUtils';

/**
 * Determine the base marker icon
 * @param {Object} call - The call object in the call queue
 * @param {Boolean} isAllView - Flag for the all view
 * @param {Boolean} isMostRecent - Flag for the most recent call for the call
 * @param {Boolean} isSecondary - Flag for indicating if there is a secondary location
 * @param {Boolean} hasCallStatusAccess - Flag for indicating if it has call status access
 * @param {Boolean} isOpaque - Flag for indicating if the marker should be opaque (not transparent)
 */
export const getCallMarkerIcon = ({
  call,
  isAllView,
  isMostRecent,
  isSecondary,
  hasCallStatusAccess,
  isOpaque = true,
}) => {
  const location = call?.core?.locations[0];
  const sourceIsSms =
    location?.source === SOURCE_SMS ||
    location?.class_of_service_category === TEXT;
  const isTT911 = call?.isTT911;
  const isLandlineOrOthers =
    (location && location?.class_of_service_category === LANDLINE) ||
    (location && location?.class_of_service_category === OTHER);

  const isVOIP = location?.class_of_service_category === VOIP;

  const callStatus =
    call?.callStatuses?.[call?.callStatuses?.length - 1] || null;
  const isCallRinging = callStatus === BE_CALL_ALERTING;
  const isCallAnswered =
    callStatus === BE_CALL_ANSWERED || callStatus === BE_RETRIEVE_CALL;
  const isCallOnHold = callStatus === BE_HOLD_CALL;
  const isCallReleased = callStatus === BE_END_CALL;

  if (isIgnoreType(call)) return null;
  if (isAllView) {
    if (sourceIsSms || isTT911) {
      return isOpaque ? AllCallSMS : AllCallSMSTransparent;
    } else if (hasCallStatusAccess && callStatus) {
      if (isCallRinging)
        return isOpaque ? CallStatusRinging : CallStatusRingingTransparent;
      else if (isCallAnswered)
        return isOpaque ? CallStatusAnswered : CallStatusAnsweredTransparent;
      else if (isCallOnHold)
        return isOpaque ? CallStatusOnHold : CallStatusOnHoldTransparent;
      else if (isCallReleased)
        return isOpaque ? CallStatusReleased : CallStatusReleasedTransparent;
      else return isOpaque ? AllCallMarker : AllCallMarkerTransparent;
    } else {
      return isOpaque ? AllCallMarker : AllCallMarkerTransparent;
    }
  } else {
    if (!isMostRecent) {
      return HistoricMarker;
    } else if (isSecondary) {
      return CallSecondary;
    } else if (isLandlineOrOthers) {
      return CallLandline;
    } else if (isVOIP) {
      return CallVoip;
    } else if (sourceIsSms || isTT911) {
      return SMSSelected;
    } else {
      return CallSelected;
    }
  }
};

/**
 * Determine the on hover marker icon
 * @param {Object} call - The call object in the call queue
 * @param {Boolean} isAllView - Flag for the all view
 * @param {Boolean} isMostRecent - Flag for the most recent call for the call
 * @param {Boolean} hasCallStatusAccess - Flag for indicating if it has call status access
 * @param {Boolean} isOpaque - Flag for indicating if the marker should be opaque (not transparent)
 */
export const getCallMarkerHoverIcon = ({
  call,
  isAllView,
  isMostRecent,
  hasCallStatusAccess,
  isOpaque = true,
}) => {
  const location = call?.core?.locations[0];
  const sourceIsSms =
    location?.source === SOURCE_SMS ||
    location?.class_of_service_category === TEXT;
  const isTT911 = call?.isTT911;
  const isLandlineOrOthers =
    (location && location?.class_of_service_category === LANDLINE) ||
    (location && location?.class_of_service_category === OTHER);

  const isVOIP = location?.class_of_service_category === VOIP;

  const callStatus =
    call?.callStatuses?.[call?.callStatuses?.length - 1] || null;
  const isCallRinging = callStatus === BE_CALL_ALERTING;
  const isCallAnswered =
    callStatus === BE_CALL_ANSWERED || callStatus === BE_RETRIEVE_CALL;
  const isCallOnHold = callStatus === BE_HOLD_CALL;
  const isCallReleased = callStatus === BE_END_CALL;

  if (isIgnoreType(call)) return null;
  if (isAllView) {
    if (sourceIsSms || isTT911) {
      return isOpaque ? AllCallSMSHover : AllCallSMSTransparentHover;
    } else if (hasCallStatusAccess && callStatus) {
      if (isCallRinging)
        return isOpaque
          ? CallStatusRingingHover
          : CallStatusRingingTransparentHover;
      else if (isCallAnswered)
        return isOpaque
          ? CallStatusAnsweredHover
          : CallStatusAnsweredTransparentHover;
      else if (isCallOnHold)
        return isOpaque
          ? CallStatusOnHoldHover
          : CallStatusOnHoldTransparentHover;
      else if (isCallReleased)
        return isOpaque
          ? CallStatusReleasedHover
          : CallStatusReleasedTransparentHover;
      else return isOpaque ? AllCallMarkerHover : AllCallMarkerHoverTransparent;
    } else {
      return isOpaque ? AllCallMarkerHover : AllCallMarkerHoverTransparent;
    }
  } else {
    if (!isMostRecent) {
      return HistoricMarkerHover;
    } else if (isLandlineOrOthers) {
      return CallLandline;
    } else if (isVOIP) {
      return CallVoip;
    } else if (sourceIsSms || isTT911) {
      return SMSSelected;
    } else {
      return CallSelected;
    }
  }
};

export const getCallMarkerSize = (isAllView, isMostRecent, call) => {
  const location = call?.core?.locations[0];
  const sourceIsSms =
    location?.source === SOURCE_SMS ||
    location?.class_of_service_category === TEXT;
  const isTT911 = call?.isTT911;

  if (isAllView) {
    if (sourceIsSms || isTT911) {
      return {
        height: 20,
        width: 23,
      };
    }
    return {
      height: 25,
      width: 20,
    };
  } else {
    if (sourceIsSms || isTT911) {
      return {
        height: !isMostRecent ? 14 : 20,
        width: !isMostRecent ? 14 : 23,
      };
    }
    return {
      height: !isMostRecent ? 14 : 24,
      width: !isMostRecent ? 14 : 24,
    };
  }
};

export const getCallCircleColor = isTransparent => {
  return [6, 192, 139, isTransparent ? 0 : 0.35];
};
